import React, { useCallback, useEffect, useState } from 'react';
import { CardStats } from '@/components/Card';
import { DateRangeFilter } from '@/components/DatePicker';
import ShiftAttendanceService, {
  ShiftAttendanceMitra,
  ShiftAttendanceRider,
  ShiftAttendanceStore,
  ShiftAttendanceSummary,
} from '@/services/api/shiftAttendance';

// Shift attendance dashboard — renders the frozen snapshots the scheduler
// captured per shift (attendance rate = on-time clock-ins / total clock-ins,
// on-time = clock-in within the shift's tolerance of its window start).
// Shift tabs are dynamic from the data: a new shift only needs a new
// Cloud Scheduler job, never a frontend change.
//
// Timezone: snapshot dates are business dates local to each store's zone, so
// the date-range filter sends plain YYYY-MM-DD strings with no conversion —
// and clock-in times are rendered in the STORE's timezone, not the viewer's.

const ALL_SHIFTS = 'ALL';
/** Riders shown before the list asks to be expanded. */
const RIDER_PREVIEW_COUNT = 10;

const TZ_LABEL: Record<string, string> = {
  'Asia/Jakarta': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
};

/** SHIFT_1 → "Shift 1"; anything else title-cased from its code. */
function shiftLabel(code: string): string {
  const match = /^SHIFT_(\d+)$/.exec(code);
  if (match) return `Shift ${match[1]}`;
  return code
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Local Y-M-D (viewer's calendar) — deliberately NOT toISOString, which
 *  would shift the date across midnight UTC. */
function formatDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Clock-in time rendered on the STORE's clock, e.g. "06:05". */
function isoToTimeInZone(iso: string, timezone: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  try {
    return d.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    });
  } catch {
    // Unknown zone string — fall back to the viewer's clock.
    return d.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
}

function shortDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`);
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
}

const fmtRate = (rate: number): string =>
  `${(rate * 100).toFixed(1).replace(/\.0$/, '')}%`;

/** 197 → "3h 17m"; 45 → "45m". */
function humanLate(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h <= 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function RatePill({ rate, clockIn }: { rate: number; clockIn: number }) {
  if (clockIn === 0) return <span className="text-sm text-gray-400">-</span>;
  const style =
    rate >= 0.9
      ? 'bg-green-50 text-green-600'
      : rate >= 0.75
        ? 'bg-yellow-50 text-yellow-600'
        : 'bg-red-50 text-red-500';
  const icon =
    rate >= 0.9
      ? 'ri-checkbox-circle-fill'
      : rate >= 0.75
        ? 'ri-error-warning-fill'
        : 'ri-close-circle-fill';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${style}`}
    >
      <i className={`${icon} text-[13px]`} />
      {fmtRate(rate)}
    </span>
  );
}

function MitraRow({
  mitra,
  multiDay,
}: {
  mitra: ShiftAttendanceMitra;
  multiDay: boolean;
}) {
  const late = mitra.status === 'LATE';
  return (
    <div
      className={`flex items-center gap-3 px-3.5 py-2.5 border-b border-gray-100 last:border-b-0 ${
        late ? 'shadow-[inset_3px_0_0_#f59e0b]' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 leading-tight truncate">
          {mitra.driverName}
        </p>
        <p className="text-xs text-gray-400 leading-tight">
          {mitra.driverCode ?? '-'}
          {multiDay && ` · ${shortDate(mitra.date)}`}
        </p>
      </div>
      <span className="shrink-0 px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold whitespace-nowrap hidden sm:inline-flex">
        {shiftLabel(mitra.shiftCode)}
      </span>
      <span className="shrink-0 min-w-[72px] text-right text-sm font-semibold text-gray-900 tabular-nums">
        {isoToTimeInZone(mitra.clockInAt, mitra.timezone)}
        <small className="ml-1 text-[10px] font-semibold text-gray-400">
          {TZ_LABEL[mitra.timezone] ?? mitra.timezone}
        </small>
      </span>
      <span
        className={`shrink-0 sm:min-w-[110px] text-right text-xs font-semibold whitespace-nowrap ${
          late ? 'text-yellow-600' : 'text-green-600'
        }`}
      >
        {late ? `Late by ${humanLate(mitra.lateMinutes)}` : 'On time'}
      </span>
    </div>
  );
}

function MitraList({
  mitras,
  loading,
  multiDay,
}: {
  mitras: ShiftAttendanceMitra[];
  loading: boolean;
  multiDay: boolean;
}) {
  if (loading) {
    return (
      <div className="px-4 py-6 bg-gray-50 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-xl text-purple-500" />
      </div>
    );
  }
  const lateMitras = mitras.filter((m) => m.status === 'LATE');
  const onTimeMitras = mitras.filter((m) => m.status !== 'LATE');
  return (
    <div className="px-4 pb-4 pt-2 bg-gray-50">
      <div className="flex flex-wrap items-center gap-2.5 px-0.5 pb-2">
        <span className="text-sm font-semibold text-gray-900">
          {mitras.length} mitra clocked in
        </span>
        {onTimeMitras.length > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-green-50 text-green-600 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {onTimeMitras.length} on time
          </span>
        )}
        {lateMitras.length > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-yellow-50 text-yellow-600 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
            {lateMitras.length} late
          </span>
        )}
      </div>
      {lateMitras.length > 0 && (
        <>
          <p className="px-0.5 pt-1.5 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-gray-400">
            Late ({lateMitras.length})
          </p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {lateMitras.map((m) => (
              <MitraRow
                key={`${m.date}-${m.shiftCode}-${m.driverID}`}
                mitra={m}
                multiDay={multiDay}
              />
            ))}
          </div>
        </>
      )}
      {onTimeMitras.length > 0 && (
        <>
          <p className="px-0.5 pt-2.5 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-gray-400">
            On time ({onTimeMitras.length})
          </p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {onTimeMitras.map((m) => (
              <MitraRow
                key={`${m.date}-${m.shiftCode}-${m.driverID}`}
                mitra={m}
                multiDay={multiDay}
              />
            ))}
          </div>
        </>
      )}
      {mitras.length === 0 && (
        <p className="px-0.5 py-3 text-sm text-gray-400">
          No clock-ins captured for this filter.
        </p>
      )}
    </div>
  );
}

const ShiftDashboardPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    return { from, to };
  });
  const [shiftCode, setShiftCode] = useState<string>(ALL_SHIFTS);
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState<ShiftAttendanceSummary | null>(null);
  const [loading, setLoading] = useState(false);

  // Lazy mitra snapshots per store, fetched on first expand for the current
  // range/shift and reset whenever the filters change.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [mitrasByStore, setMitrasByStore] = useState<
    Map<number, ShiftAttendanceMitra[]>
  >(new Map());
  const [mitrasLoading, setMitrasLoading] = useState<Set<number>>(new Set());
  const [riders, setRiders] = useState<ShiftAttendanceRider[]>([]);
  // The API returns worst-first; flipping is a view concern, not a refetch.
  const [riderOrder, setRiderOrder] = useState<'late' | 'ontime'>('late');
  const [showAllRiders, setShowAllRiders] = useState(false);

  const startDate = formatDate(dateRange.from);
  const endDate = formatDate(dateRange.to);
  const multiDay = !!startDate && !!endDate && startDate !== endDate;

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setExpanded(new Set());
    setMitrasByStore(new Map());
    setShowAllRiders(false);
    try {
      const params = {
        startDate,
        endDate,
        shiftCode: shiftCode === ALL_SHIFTS ? undefined : shiftCode,
      };
      // The ranking reads the same snapshots as the summary, so the two are
      // always in step; a failed ranking must not blank out the whole page.
      const [data, riderRows] = await Promise.all([
        ShiftAttendanceService.getSummary(params),
        ShiftAttendanceService.getRiders(params).catch((err) => {
          console.error('Failed to fetch rider ranking:', err);
          return [] as ShiftAttendanceRider[];
        }),
      ]);
      setSummary(data);
      setRiders(riderRows);
      if (shiftCode !== ALL_SHIFTS && !data.shiftCodes.includes(shiftCode)) {
        setShiftCode(ALL_SHIFTS);
      }
    } catch (err) {
      console.error('Failed to fetch shift attendance summary:', err);
      setSummary(null);
      setRiders([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, shiftCode]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const toggleExpanded = async (pitstopID: number) => {
    if (expanded.has(pitstopID)) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(pitstopID);
        return next;
      });
      return;
    }
    setExpanded((prev) => new Set(prev).add(pitstopID));
    if (mitrasByStore.has(pitstopID)) return;

    setMitrasLoading((prev) => new Set(prev).add(pitstopID));
    try {
      const mitras = await ShiftAttendanceService.getMitras({
        pitstopID,
        startDate,
        endDate,
        shiftCode: shiftCode === ALL_SHIFTS ? undefined : shiftCode,
      });
      setMitrasByStore((prev) => new Map(prev).set(pitstopID, mitras));
    } catch (err) {
      console.error('Failed to fetch mitra snapshot:', err);
    } finally {
      setMitrasLoading((prev) => {
        const next = new Set(prev);
        next.delete(pitstopID);
        return next;
      });
    }
  };

  const stores = (summary?.stores ?? [])
    .filter((s) =>
      (s.pitstopName ?? '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => b.rate - a.rate);

  // The API already ranks worst-first; "paling tepat waktu" re-sorts rather
  // than reverses, so the more-evidence tie-break still points the same way
  // (a 100% rider with 20 clock-ins outranks one with 2).
  const orderedRiders =
    riderOrder === 'late'
      ? riders
      : [...riders].sort((a, b) => b.rate - a.rate || b.clockIn - a.clockIn);
  const visibleRiders = showAllRiders
    ? orderedRiders
    : orderedRiders.slice(0, RIDER_PREVIEW_COUNT);

  const totals = summary?.totals;
  const rangeLabel = summary
    ? summary.startDate === summary.endDate
      ? summary.startDate
      : `${summary.startDate} – ${summary.endDate}`
    : '';

  return (
    <div className="bg-white p-6 max-w-full mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Shift Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Rekap kehadiran mitra per store di setiap shift. Attendance rate
          adalah persentase mitra yang clock-in tepat waktu dari total yang
          clock-in, mengikuti jam lokal masing-masing store.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <div className="w-full sm:w-auto">
          <DateRangeFilter
            initialRange={dateRange}
            onDateRangeChange={(range) => setDateRange(range)}
          />
        </div>

        {/* Shift tabs — dynamic from captured shiftCodes */}
        <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden bg-white w-max">
          <button
            onClick={() => setShiftCode(ALL_SHIFTS)}
            className={`px-3.5 h-9 text-sm font-medium border-r border-gray-200 last:border-r-0 transition-colors ${
              shiftCode === ALL_SHIFTS
                ? 'bg-purple-50 text-purple-700'
                : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            All Shifts
          </button>
          {(summary?.shiftCodes ?? []).map((code) => (
            <button
              key={code}
              onClick={() => setShiftCode(code)}
              className={`px-3.5 h-9 text-sm font-medium border-r border-gray-200 last:border-r-0 transition-colors whitespace-nowrap ${
                shiftCode === code
                  ? 'bg-purple-50 text-purple-700'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {shiftLabel(code)}
            </button>
          ))}
        </div>

        <div className="hidden sm:flex flex-1" />

        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[16px]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search store..."
            className="h-9 pl-9 pr-4 w-full sm:w-64 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CardStats
          title="Attendance Rate"
          value={totals && totals.clockIn > 0 ? fmtRate(totals.rate) : '-'}
          icon="ri-checkbox-circle-line"
          color="purple"
          subtitle={
            totals
              ? `${totals.onTime} on-time of ${totals.clockIn} clock-in · ${totals.late} late`
              : undefined
          }
          loading={loading}
        />
        <CardStats
          title="Total Attendance"
          value={totals ? totals.clockIn : '-'}
          icon="ri-group-line"
          color="blue"
          subtitle={
            summary
              ? `clock-ins ${rangeLabel} across ${stores.length} store${stores.length === 1 ? '' : 's'}`
              : undefined
          }
          loading={loading}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-6">
          <i className="ri-loader-4-line animate-spin text-2xl text-purple-500" />
        </div>
      )}

      {/* Per-store table with expandable mitra snapshot */}
      {!loading && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {[
                  'Store / Pitstop',
                  'Total Clock In',
                  'On-time',
                  'Late',
                  'Attendance Rate',
                ].map((title) => (
                  <th
                    key={title}
                    className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap"
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stores.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <h3 className="text-base font-medium text-gray-900 mb-1">
                      No Snapshot in This Range Yet
                    </h3>
                    <p className="text-sm text-gray-500 max-w-sm mx-auto">
                      Shift attendance appears here after the scheduler
                      captures each shift. Widen the date range or check back
                      after the next capture runs.
                    </p>
                  </td>
                </tr>
              )}
              {stores.map((store) => (
                <React.Fragment key={store.pitstopID}>
                  <tr
                    onClick={() => toggleExpanded(store.pitstopID)}
                    className="border-b border-gray-100 cursor-pointer hover:bg-purple-50/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <i
                          className={`ri-arrow-right-s-line text-[16px] transition-transform ${
                            expanded.has(store.pitstopID)
                              ? 'rotate-90 text-purple-600'
                              : 'text-gray-400'
                          }`}
                        />
                        <span className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center shrink-0">
                          <i className="ri-store-2-line text-[15px]" />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900 leading-tight">
                            {store.pitstopName ??
                              `Pitstop #${store.pitstopID}`}
                          </p>
                          <p className="text-xs text-gray-400 leading-tight">
                            {TZ_LABEL[store.timezone] ?? store.timezone}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">
                      {store.clockIn}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600 tabular-nums">
                      {store.onTime}
                    </td>
                    <td
                      className={`px-4 py-3 text-sm tabular-nums ${
                        store.late > 0
                          ? 'font-medium text-yellow-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {store.late || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <RatePill rate={store.rate} clockIn={store.clockIn} />
                    </td>
                  </tr>
                  {expanded.has(store.pitstopID) && (
                    <tr className="border-b border-gray-100">
                      <td colSpan={5} className="p-0">
                        <MitraList
                          mitras={mitrasByStore.get(store.pitstopID) ?? []}
                          loading={mitrasLoading.has(store.pitstopID)}
                          multiDay={multiDay}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rider ranking */}
      {!loading && riders.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Ranking Kehadiran Mitra
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Baca bareng kolom Hari Aktif — mitra yang baru masuk 2 hari
                belum bisa dibandingin sama yang udah 20 hari.
              </p>
            </div>
            <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden bg-white w-max">
              {(
                [
                  ['late', 'Paling sering telat'],
                  ['ontime', 'Paling tepat waktu'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setRiderOrder(value)}
                  className={`px-3 h-8 text-xs font-medium border-r border-gray-200 last:border-r-0 transition-colors whitespace-nowrap ${
                    riderOrder === value
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {[
                    'Mitra',
                    'Hari Aktif',
                    'Total Clock In',
                    'On-time',
                    'Late',
                    'Rata-rata Telat',
                    'Attendance Rate',
                  ].map((title) => (
                    <th
                      key={title}
                      className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap"
                    >
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRiders.map((rider) => (
                  <tr
                    key={rider.driverID}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 leading-tight">
                        {rider.driverName}
                      </p>
                      <p className="text-xs text-gray-400 leading-tight">
                        {rider.driverCode ?? '-'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">
                      {rider.daysActive}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">
                      {rider.clockIn}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600 tabular-nums">
                      {rider.onTime}
                    </td>
                    <td
                      className={`px-4 py-3 text-sm tabular-nums ${
                        rider.late > 0
                          ? 'font-medium text-yellow-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {rider.late || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 tabular-nums whitespace-nowrap">
                      {rider.late > 0 ? humanLate(rider.avgLateMinutes) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <RatePill rate={rider.rate} clockIn={rider.clockIn} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {riders.length > RIDER_PREVIEW_COUNT && (
            <button
              onClick={() => setShowAllRiders((v) => !v)}
              className="text-xs font-semibold text-purple-600 hover:text-purple-700"
            >
              {showAllRiders
                ? 'Tampilkan 10 teratas'
                : `Tampilkan semua ${riders.length} mitra`}
            </button>
          )}
        </div>
      )}

      {summary?.capturedAt && (
        <p className="text-xs text-gray-400">
          Latest snapshot captured at{' '}
          {new Date(summary.capturedAt).toLocaleString('id-ID')}
        </p>
      )}
    </div>
  );
};

export default ShiftDashboardPage;
